import {isNil} from 'ramda';
import {isArray, isString} from 'ramda-adjunct';

import type {Nullish} from 'shared/web';

import {useSliderFilter} from './useSliderFilter';

type RangeModel =
  [number | null, number | null] | string | Nullish | {from?: number | null; to?: number | null};

type ValueTransforms = {
  toDisplay: (value: number) => number;
  fromDisplay: (value: number) => number;
};

type RangeSliderFilterConfig = {
  model: RangeModel;
  onModelChange: (newValue: unknown) => void;
  min: number;
  max: number;
  /** Stepper increment in DISPLAY units (BE stepSize). Defaults to 1. */
  step?: number;
  valueTransforms?: ValueTransforms;
};

const identity = (value: number) => value;

export const useRangeSliderFilter = (config: RangeSliderFilterConfig) => {
  const toDisplay = config.valueTransforms?.toDisplay ?? identity;
  const fromDisplay = config.valueTransforms?.fromDisplay ?? identity;
  const step = config.step ?? 1;

  const [filterValue, setFilterValue] = useSliderFilter({
    model: config.model,
    onModelChange: config.onModelChange,
    min: config.min,
    max: config.max,
  });

  const onSliderChange = (value: [number | null, number | null] | null) => {
    setFilterValue(value);
  };

  // Apply a quick-filter selection: the model becomes a sentinel string, so the
  // derived slider thumbs fall back to the full [min, max] span automatically.
  const applyQuickFilter = (newValue: Parameters<typeof setFilterValue>[0]) => {
    setFilterValue(newValue);
  };

  const onFromChange = (value: number | null, event?: unknown) => {
    let displayValue = value === null || isNaN(value) ? null : value;

    const isStepper = event === undefined && displayValue !== null;

    // Empty From bound + stepper (event === undefined — typing always carries
    // the input event): the platform steps from the DISPLAYED value, and both
    // consumers omit minStepperValue while the bound is empty, so the platform
    // emits ±step artifacts and the direction is known. An empty From bound
    // effectively IS min: minus materializes the bound at min, plus lands one
    // step above min.
    if (isStepper && filterValue[0] === null) {
      if (displayValue === -step) {
        // Materializing an edge produces a filter-nothing range; doing that
        // while a quick-filter sentinel is active would silently replace the
        // sentinel — keep the quick filter instead.
        if (isString(config.model)) {
          return;
        }
        // from: min survives only a direct emit — updateGridFilter normalizes
        // "from at min + to empty" to an empty filter. Never cross a set To
        // bound (an inverted range must not reach the model).
        const toBound = filterValue[1] ?? config.max;
        config.onModelChange({from: Math.min(config.min, toBound), to: toBound});
        return;
      }
      if (displayValue === step) {
        displayValue = Math.min(
          toDisplay(config.min) + step,
          toDisplay(filterValue[1] ?? config.max),
          toDisplay(config.max)
        );
      }
    }

    if (isStepper && displayValue !== null) {
      // Without minStepperValue the platform no longer clamps decrements at
      // min, and a stepper that lands on the current value (minus at min) is
      // a no-op.
      displayValue = Math.max(displayValue, toDisplay(config.min));
      if (filterValue[0] !== null && displayValue === toDisplay(filterValue[0])) {
        return;
      }
      // A decrement on a SET From bound that lands on min must KEEP the bound
      // at min (the input shows min) — the from===min normalization below
      // would clear the bound and empty the input instead.
      if (filterValue[0] !== null && displayValue === toDisplay(config.min)) {
        const toBound = filterValue[1] ?? config.max;
        config.onModelChange({from: Math.min(config.min, toBound), to: toBound});
        return;
      }
    }

    const valueToSet = displayValue === null ? null : fromDisplay(displayValue);

    if (isNil(filterValue)) {
      onSliderChange([valueToSet, null]);
    }

    if (isArray(filterValue)) {
      const isFromNilOrLowestPossible = isNil(valueToSet) || valueToSet === config.min;
      const isToNilOrLowestPossible = isNil(filterValue?.[1]) || filterValue?.[1] === config.max;

      if (isFromNilOrLowestPossible && isToNilOrLowestPossible) {
        onSliderChange(null);
      } else {
        onSliderChange([valueToSet, filterValue?.[1] === 0 ? null : filterValue?.[1]]);
      }
    }

    if (isString(filterValue)) {
      onSliderChange([valueToSet, config.max]);
    }
  };

  const onToChange = (value: number | null, event?: unknown) => {
    let displayValue = value === null || isNaN(value) ? null : value;

    const isStepper = event === undefined && displayValue !== null;

    // NumberInput steppers/arrow keys (event === undefined — typing always
    // carries the input event) compute their result from the DISPLAYED value,
    // and both consumers omit minStepperValue while the bound is empty, so the
    // platform emits ±step artifacts and the direction is known. An empty To
    // bound effectively IS max: plus jumps to max, minus lands one step below.
    if (isStepper && filterValue[1] === null) {
      if (displayValue === step) {
        // Materializing an edge produces a filter-nothing range; doing that
        // while a quick-filter sentinel is active would silently replace the
        // sentinel — keep the quick filter instead.
        if (isString(config.model)) {
          return;
        }
        // to: max survives only a direct emit — updateGridFilter normalizes
        // "from empty + to at max" to an empty filter.
        config.onModelChange({from: filterValue[0] ?? config.min, to: config.max});
        return;
      }
      if (displayValue === -step) {
        // Never cross a set From bound (an inverted range must not reach the
        // model).
        displayValue = Math.max(
          toDisplay(config.max) - step,
          toDisplay(filterValue[0] ?? config.min)
        );
      }
    }

    if (isStepper && displayValue !== null) {
      // Without minStepperValue the platform no longer clamps decrements at
      // min, and a stepper that lands on the current value (plus at max) is a
      // no-op. NOTE: a decrement that overshoots min lands on 0 and the 0→null
      // coercion CLEARS the bound (input empties; the next minus then treats
      // the bound as open). Swallowing it instead is NOT viable consumer-side:
      // the platform paints its clamped value before onChange, and only a
      // props.value change resyncs the display — a swallowed emit leaves the
      // input showing 0 against a non-0 model.
      displayValue = Math.max(displayValue, toDisplay(config.min));
      if (filterValue[1] !== null && displayValue === toDisplay(filterValue[1])) {
        return;
      }
      // Mirror of the From side: an increment on a SET To bound that lands on
      // max keeps the bound at max instead of being cleared by the to===max
      // normalization below.
      if (filterValue[1] !== null && displayValue === toDisplay(config.max)) {
        config.onModelChange({from: filterValue[0] ?? config.min, to: config.max});
        return;
      }
    }

    const valueToSet = displayValue === null ? null : fromDisplay(displayValue);

    if (isNil(filterValue)) {
      onSliderChange([null, valueToSet]);
    }

    if (isArray(filterValue)) {
      const isFromNilOrLowestPossible = isNil(filterValue?.[0]) || filterValue?.[0] === config.min;
      const isToNilOrLowestPossible = isNil(valueToSet) || valueToSet === config.max;

      if (isFromNilOrLowestPossible && isToNilOrLowestPossible) {
        onSliderChange(null);
      } else {
        onSliderChange([filterValue?.[0] === 0 ? null : filterValue?.[0], valueToSet]);
      }
    }

    if (isString(filterValue)) {
      onSliderChange([config.min, valueToSet]);
    }
  };

  const quickFilterValue = isString(config.model) ? [config.model] : null;

  // Thumb positions derive from the applied model (via filterValue) — a bound left
  // open falls back to the corresponding edge. No local thumb state: reopening the
  // filter, preset switches and clear-all therefore always show the applied range.
  const sliderValue: [number, number] = [
    filterValue[0] ?? config.min,
    filterValue[1] ?? config.max,
  ];

  const fromValue = isArray(filterValue) // eslint-disable-line no-nested-ternary -- Pre-existing violation
    ? filterValue?.[0] !== null
      ? toDisplay(filterValue[0]!)
      : null
    : null;

  const toValue = isArray(filterValue) // eslint-disable-line no-nested-ternary -- Pre-existing violation
    ? filterValue?.[1] !== null
      ? toDisplay(filterValue[1]!)
      : null
    : null;

  return {
    filterValue,
    setFilterValue,
    applyQuickFilter,
    onSliderChange,
    onFromChange,
    onToChange,
    quickFilterValue,
    sliderValue,
    fromValue,
    toValue,
  } as const;
};
